import React, { useEffect, useRef } from 'react';
import { Calendar, Target, TrendingUp } from 'lucide-react';
import styles from './Steps.module.css';
import Button from '../button/Button';

const WorkflowTimeline = () => {
  const timelineRef = useRef(null);
  const circlesRef = useRef([]);
  const cardsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.visible);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    // Observe circles
    circlesRef.current.forEach((circle) => {
      if (circle) observer.observe(circle);
    });

    // Observe cards
    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      number: 1,
      title: "אסטרטגיה ומסרים שמחממים לידים",
      description: "אנחנו מפצחות לעומק מי הלקוח/ה שלך, מה הערך הייחודי שלך, ומה גורם לאנשים לבחור בך ולא באחרים.",
      subtitle: "ביחד נבנה:",
      items: [
        "מסרים שמרגישים כמו את, לא כמו כולם",
        "בידול ברור שמונע התמקחויות מיותרות",
        "שפה שיווקית שמחברת רגשית ומייצרת אמון"
      ],
      result: "בהירות. שיווק שיש לו כיוון, לא ניחושים."
    },
    {
      number: 2,
      title: "יום צילום ובנק תוכן שמוכר",
      description: "מגיע הרגע שבו המצלמה הופכת מ״לחץ״ לכלי מכירה.",
      subtitle: "בשלב הזה אנחנו:",
      items: [
        "מכינות שאלות ותסריטים טבעיים (לא רובוטיים)",
        "מצלמות תכנים לאורגני + לממומן (למי שמתאים)",
        "יוצרות בנק תוכן לחודש–חודשיים קדימה, סיסטם אמיתי"
      ],
      result: "תוכן שממשיך למכור גם כשאת לא אונליין."
    },
    {
      number: 3,
      title: "הפצה, מדידה וליווי מתמשך",
      description: "אחרי שיש תוכן - אנחנו מפיקות ממנו את המקסימום.",
      subtitle: "בתהליך הזה את מקבלת:",
      items: [
        "פיצול עריכה ומסירת סרטונים מוכנים לשימוש",
        "הנחיות איך להעלות נכון כדי לחמם לידים",
        "מדידה, ניתוח, ושיפור שבועי/חודשי"
      ],
      result: "השיווק שלך נהיה מדויק יותר, זול יותר, וממיר יותר."
    }
  ];

  return <>
    <section className={styles.timelineSection}>
      <div className={styles.container}>
        <h2 className={styles.mainTitle}>
          תחזיקו חזק - ככה אנחנו בונות תשתית שיווקית ב־90 יום שמתחילה להביא לידים איכותיים.
        </h2>
        
        <p className={styles.subtitle}>
          אנחנו מתחילות ב־90 יום שמקימים תשתית שיווקית שמתחילה לייצר לידים איכותיים. 
          אחרי שהמערכת עובדת - מי שרוצה ממשיכה איתי לריטיינר שמגדיל ומייצב את התוצאות לאורך זמן.
        </p>

        <div className={styles.timeline} ref={timelineRef}>
          {steps.map((step, index) => (
            <div key={step.number} className={styles.timelineItem}>
              {/* Connection Line */}
              {index < steps.length - 1 && (
                <div className={styles.connectionLine}></div>
              )}

              {/* Step Number Circle */}
              <div 
                className={styles.stepCircle}
                ref={(el) => (circlesRef.current[index] = el)}
              >
                <span className={styles.stepNumber}>{step.number}</span>
              </div>

              {/* Step Content */}
              <div 
                className={styles.stepContent}
                ref={(el) => (cardsRef.current[index] = el)}
              >
                <h3 className={styles.stepTitle}>{step.title}</h3>
                <p className={styles.stepDescription}>{step.description}</p>
                
                {step.subtitle && (
                  <p className={styles.stepSubtitle}>{step.subtitle}</p>
                )}
                
                <ul className={styles.stepList}>
                  {step.items.map((item, itemIndex) => (
                    <li key={itemIndex} className={styles.stepListItem}>
                      <span className={styles.bullet}>•</span>
                      <span className={styles.itemText}>{item}</span>
                    </li>
                  ))}
                </ul>

                <div className={styles.resultBox}>
                  <span className={styles.resultLabel}>התוצאה:</span>
                  <span className={styles.resultText}>{step.result}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* After 90 Days Section */}
        <div className={styles.afterSection}>
          <h3 className={styles.afterTitle}>✦ ומה קורה אחרי 90 יום?</h3>
          <p className={styles.afterText}>
            אם היסודות עובדים ואת רוצה לגדול - אנחנו עוברים לריטיינר חודשי שכולל (לפי צורך):
          </p>
          
          <div className={styles.retainerFeatures}>
            <div className={styles.featureItem}>
              <span className={styles.featureEmoji}>🎥</span>
              <span className={styles.featureText}>ימי צילום נוספים</span>
            </div>
            <div className={styles.featureItem}>
              <span className={styles.featureEmoji}>🚀</span>
              <span className={styles.featureText}>קידום ממומן והרחבת קהלים</span>
            </div>
            <div className={styles.featureItem}>
              <span className={styles.featureEmoji}>📈</span>
              <span className={styles.featureText}>שיפור מסרים וקריאייטיב</span>
            </div>
            <div className={styles.featureItem}>
              <span className={styles.featureEmoji}>🤝</span>
              <span className={styles.featureText}>ליווי צמוד שמחזיק צמיחה יציבה</span>
            </div>
          </div>
        </div>
      </div>
    </section>
    <Button text="אני רוצה לשמוע עוד הילה!"/>
  </>
};

export default WorkflowTimeline;